import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "./ProductManager.css";
import { useSelector } from "react-redux";

function UpdateProduct() {
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedProducts, setExpandedProducts] = useState({});
  const [editingItem, setEditingItem] = useState(null);

  const currentUser = useSelector((state) => state.AuthReducer?.user);

  useEffect(() => {
    fetchProducts();
    fetchLocations();
  }, []);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/ProductV/getProductV", {
        headers: { Authorization: token ? `Bearer ${token}` : "" }
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Erreur chargement produits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get("/api/Locations/getAllLocations");
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur localisations:", err);
    }
  };

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return loc.trim();
    return loc._id || loc.$oid || loc.id || null;
  };

  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const compressImage = (file, targetKB) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const MAX_DIM = 800;
          if (width > height && width > MAX_DIM) {
            height = (height * MAX_DIM) / width;
            width = MAX_DIM;
          } else if (height > MAX_DIM) {
            width = (width * MAX_DIM) / height;
            height = MAX_DIM;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.7;
          let base64 = canvas.toDataURL("image/jpeg", quality);
          resolve(base64);
        };
      };
    });
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      setIsLoading(true);
      const { type, data } = editingItem;
      const token = localStorage.getItem("token");
      const url = type === 'product'
        ? `/api/ProductV/updateProductV/${data._id}`
        : `/api/ProductV/updateSubProductV/${data._id}`;

      // Nettoyer le payload pour n'envoyer que ce qui est nécessaire
      let payload = {};

      if (type === 'product') {
        payload = {
          name: data.name,
          category: data.category,
          price: data.price ? parseFloat(data.price) : 0,
          localisation: extractLocId(data.localisation)
        };
      } else {
        payload = {
          name: data.name,
          price: parseFloat(data.price),
          barcode: data.barcode,
          localisation: extractLocId(data.localisation)
        };
      }

      if (data.newImageFile) {
        payload.image = await compressImage(data.newImageFile, 128);
      }

      const response = await axios.put(url, payload, {
        headers: { Authorization: token ? `Bearer ${token}` : "" }
      });

      alert("✅ Mise à jour réussie !");
      setEditingItem(null);
      fetchProducts();
    } catch (error) {
      console.error("Erreur update:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Erreur lors de la mise à jour.";
      alert(`❌ ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProductHandler = async (id) => {
    if (!window.confirm("Supprimer ce produit et TOUTES ses variations ?")) return;
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      await axios.delete(`/api/ProductV/deleteProductV/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" }
      });
      fetchProducts();
    } catch (error) { alert("❌ Erreur suppression."); }
    finally { setIsLoading(false); }
  };

  const deleteSubProductHandler = async (id) => {
    if (!window.confirm("Supprimer cette variation ?")) return;
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      await axios.delete(`/api/ProductV/deleteSubProductV/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" }
      });
      fetchProducts();
    } catch (error) { alert("❌ Erreur suppression."); }
    finally { setIsLoading(false); }
  };

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      p.subProductsV?.some(sp => sp.name.toLowerCase().includes(q) || sp.barcode?.includes(q))
    );
  }, [products, searchQuery]);

  if (!currentUser) return null;

  return (
    <div className="up-container">
      <header className="up-header">
        <h2>📋 Gestion de Stock (Valises)</h2>
        <span className="up-user-badge">{currentUser.name}</span>
      </header>

      <main className="up-content">
        <div className="up-search-container">
          <span className="up-search-icon-fixed">🔍</span>
          <input
            className="up-search-input"
            type="text"
            placeholder="Rechercher par nom, catégorie ou code-barres..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="up-products-grid">
          {isLoading && <div className="pv-loader-overlay"><div className="spinner"></div></div>}

          {filteredProducts.map(product => (
            <div key={product._id} className="up-product-card">
              <div className="up-card-header" onClick={() => toggleProductExpansion(product._id)}>
                <div className="up-main-info">
                  <div className="up-img-preview">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="up-category-avatar">{product.category.charAt(0)}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="up-category-pill">{product.category}</span>
                    <h4>{product.name}</h4>
                  </div>
                </div>
                <div className="up-action-btns">
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8', marginRight: '15px' }}>
                    {product.subProductsV?.length || 0} variations
                  </span>
                  <button className="up-btn-icon" onClick={(e) => {
                    e.stopPropagation();
                    setEditingItem({ type: 'product', data: { ...product } });
                  }}>✏️</button>
                  <button className="up-btn-icon delete" onClick={(e) => {
                    e.stopPropagation();
                    deleteProductHandler(product._id);
                  }}>🗑️</button>
                  <span style={{ marginLeft: '10px', color: '#94a3b8' }}>
                    {expandedProducts[product._id] ? '▲' : '▼'}
                  </span>
                </div>
              </div>

              {expandedProducts[product._id] && (
                <div className="up-card-body">
                  <table className="up-table">
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>Nom Variation</th>
                        <th>Prix</th>
                        <th>Lieu</th>
                        <th>Barcode</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.subProductsV?.map(sp => (
                        <tr key={sp._id}>
                          <td>
                            <div className="up-table-img">
                              {sp.image ? <img src={sp.image} alt={sp.name} /> : <span>-</span>}
                            </div>
                          </td>
                          <td>{sp.name}</td>
                          <td className="up-price">{sp.price} DT</td>
                          <td>
                            <span className="up-badge-loc">
                              {locations.find(l => extractLocId(l._id) === extractLocId(sp.localisation))?.name || '-'}
                            </span>
                          </td>
                          <td><code className="up-barcode-small">{sp.barcode || '-'}</code></td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="up-action-btns" style={{ justifyContent: 'flex-end' }}>
                              <button className="up-btn-icon" onClick={() => setEditingItem({ type: 'subproduct', data: { ...sp } })}>✏️</button>
                              <button className="up-btn-icon delete" onClick={() => deleteSubProductHandler(sp._id)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>

      {/* Modal d'édition */}
      {editingItem && (
        <div className="up-modal-overlay">
          <div className="up-modal">
            <div className="up-modal-header">
              <h3>Modifier {editingItem.type === 'product' ? 'Produit Parent' : 'Variation'}</h3>
              <button className="close-btn" onClick={() => setEditingItem(null)}>×</button>
            </div>
            <div className="up-modal-body">
              {editingItem.type === 'product' ? (
                <div className="pv-form-row">
                  <div className="pv-form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      value={editingItem.data.name}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="pv-form-group">
                    <label>Catégorie</label>
                    <select
                      value={editingItem.data.category}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, category: e.target.value }
                      })}
                    >
                      <option value="VALISES">VALISES</option>
                      <option value="SACS">SACS</option>
                      <option value="ACCESSOIRES">ACCESSOIRES</option>
                      <option value="SET">SET</option>
                      <option value="JOUETS">JOUETS (Famille Générale)</option>
                      <option value="TRANSPORT">TRANSPORT</option>
                      <option value="POUPEE">POUPEE</option>
                      <option value="EDUCATIFS">EDUCATIFS</option>
                      <option value="METIERS">METIERS</option>
                      <option value="BRICOLAGE">BRICOLAGE</option>
                      <option value="PERSONNAGE">PERSONNAGE</option>
                      <option value="ANIMAUX">ANIMAUX</option>
                      <option value="FILLETTE">FILLETTE</option>
                      <option value="CUISINE">CUISINE</option>
                      <option value="MUSIQUE">MUSIQUE</option>
                      <option value="FIRSTAGE">FIRSTAGE</option>
                      <option value="CANDY BAR">CANDY BAR</option>
                      <option value="CANDY STORE">CANDY STORE</option>
                      <option value="PLAYSTATION">PLAYSTATION</option>
                    </select>
                  </div>
                  <div className="pv-form-group">
                    <label>Prix Parent (Pour les SET)</label>
                    <input
                      type="number"
                      value={editingItem.data.price || 0}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, price: e.target.value }
                      })}
                    />
                  </div>
                </div>
              ) : (
                <div className="pv-form-row">
                  <div className="pv-form-group">
                    <label>Prix (DT)</label>
                    <input
                      type="number"
                      value={editingItem.data.price}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, price: e.target.value }
                      })}
                    />
                  </div>
                  <div className="pv-form-group">
                    <label>Code-barres</label>
                    <input
                      type="text"
                      value={editingItem.data.barcode}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, barcode: e.target.value }
                      })}
                    />
                  </div>
                </div>
              )}
              <div className="pv-form-group" style={{ marginTop: '15px' }}>
                <label>Nouvelle Image (Sera compressée)</label>
                <input type="file" accept="image/*" onChange={(e) => setEditingItem({
                  ...editingItem,
                  data: { ...editingItem.data, newImageFile: e.target.files[0] }
                })} />
              </div>
            </div>
            <div className="up-modal-footer">
              <button className="up-btn-secondary" onClick={() => setEditingItem(null)}>Annuler</button>
              <button className="up-btn-primary" onClick={handleUpdateItem}>Sauvegarder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateProduct;